import { useState, useEffect, useRef } from "react";

interface UseAnimatedPlaceholderOptions {
  texts: string[]; // Array of texts to alternate
  baseText: string; // Base text that always appears first (ex: "Let's build")
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseBeforeDelete?: number;
  pauseBeforeType?: number;
  enabled?: boolean; // Whether the animation is enabled
}

export const useAnimatedPlaceholder = ({
  texts,
  baseText,
  typingSpeed = 50,
  deletingSpeed = 30,
  pauseBeforeDelete = 2000,
  pauseBeforeType = 500,
  enabled = true,
}: UseAnimatedPlaceholderOptions): string => {
  const [displayedText, setDisplayedText] = useState(baseText);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDeletingRef = useRef(false);
  const currentIndexRef = useRef(0);
  const displayedTextRef = useRef(baseText);
  const textsSignature = texts.join("\u0001");

  useEffect(() => {
    isDeletingRef.current = isDeleting;
    currentIndexRef.current = currentIndex;
    displayedTextRef.current = displayedText;
  }, [isDeleting, currentIndex, displayedText]);

  useEffect(() => {
    if (!enabled || texts.length === 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setDisplayedText(baseText);
      setIsDeleting(false);
      setCurrentIndex(0);
      isDeletingRef.current = false;
      currentIndexRef.current = 0;
      displayedTextRef.current = baseText;
      return;
    }

    const schedule = (fn: () => void, delay: number) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(fn, Math.max(0, delay));
    };

    const animate = () => {
      const currentDisplayed = displayedTextRef.current;
      const idx = currentIndexRef.current;
      const targetText = baseText + texts[idx];

      let nextDelay: number;

      if (isDeletingRef.current) {
        // Remove characters one by one until the base text is reached
        if (currentDisplayed.length > baseText.length) {
          const newText = currentDisplayed.slice(0, -1);
          setDisplayedText(newText);
          displayedTextRef.current = newText;
          nextDelay = deletingSpeed;
        } else {
          // Finished deleting, pause and then start typing the next text
          setIsDeleting(false);
          isDeletingRef.current = false;
          const nextIndex = (idx + 1) % texts.length;
          setCurrentIndex(nextIndex);
          currentIndexRef.current = nextIndex;
          nextDelay = pauseBeforeType;
        }
      } else {
        // Typing mode: add characters one by one until the text is complete
        if (currentDisplayed.length < targetText.length) {
          const nextChar = targetText[currentDisplayed.length];
          const newText = currentDisplayed + nextChar;
          setDisplayedText(newText);
          displayedTextRef.current = newText;
          nextDelay = typingSpeed;
        } else {
          // Finished typing, pause and then start deleting
          setIsDeleting(true);
          isDeletingRef.current = true;
          nextDelay = pauseBeforeDelete;
        }
      }

      schedule(animate, nextDelay);
    };

    schedule(animate, pauseBeforeType);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    enabled,
    baseText,
    textsSignature,
    typingSpeed,
    deletingSpeed,
    pauseBeforeDelete,
    pauseBeforeType,
  ]);

  return displayedText;
};

